import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { fmtUSD, fmtNum } from "@/lib/format";
import {
  Play, RotateCcw, Sparkles, TrendingUp, Building2, Users,
  FileCheck, ShieldCheck, Zap, Layers, ArrowRight, DollarSign,
  PieChart, BarChart3, Crown, Wallet, Cog, Coins, Banknote,
  Scale, Trophy, Eye, MousePointerClick, CreditCard,
} from "lucide-react";

/* ============================================================
   Revenue Stream Definitions
   ============================================================ */

interface RevenueStream {
  id: string;
  name: string;
  icon: typeof Building2;
  description: string;
  unit: string;
  rate: number;
  unitLabel: string;
  color: string;
  bg: string;
  ring: string;
  glow: string;
}

const STREAMS: RevenueStream[] = [
  { id: "transaction", name: "Transaction Fees", icon: Zap, description: "Per-payment instruction fee on every procurement settlement processed through the control plane.", unit: "per transaction", rate: 0.015, unitLabel: "1.5% of flow", color: "#059669", bg: "bg-emerald-500/10", ring: "ring-emerald-400/50", glow: "rgba(5,150,105,0.2)" },
  { id: "subscription", name: "School Subscriptions", icon: Building2, description: "Monthly SaaS license per school for the procurement control plane, audit trail, and supplier passport.", unit: "per school / month", rate: 450, unitLabel: "USD 450/mo", color: "#2563eb", bg: "bg-blue-500/10", ring: "ring-blue-400/50", glow: "rgba(37,99,235,0.2)" },
  { id: "verification", name: "Supplier Verification", icon: ShieldCheck, description: "One-time KYB onboarding and document verification fee per supplier added to the approved list.", unit: "per supplier", rate: 85, unitLabel: "USD 85", color: "#d97706", bg: "bg-amber-500/10", ring: "ring-amber-400/50", glow: "rgba(217,119,6,0.2)" },
  { id: "audit", name: "Audit Pack Exports", icon: FileCheck, description: "Premium one-click audit pack generation for boards, auditors, and funders requiring evidence bundles.", unit: "per audit pack", rate: 120, unitLabel: "USD 120", color: "#7c3aed", bg: "bg-violet-500/10", ring: "ring-violet-400/50", glow: "rgba(124,58,237,0.2)" },
  { id: "partner", name: "Partner Settlement", icon: Layers, description: "Integration fee for each licensed settlement partner connected via the controlled adapter layer.", unit: "per partner / month", rate: 2500, unitLabel: "USD 2,500/mo", color: "#0891b2", bg: "bg-cyan-500/10", ring: "ring-cyan-400/50", glow: "rgba(8,145,178,0.2)" },
  { id: "implementation", name: "Implementation & Onboarding", icon: Cog, description: "One-time setup, policy configuration, data migration, and staff training per school group.", unit: "per school group", rate: 8000, unitLabel: "USD 8,000", color: "#dc2626", bg: "bg-rose-500/10", ring: "ring-rose-400/50", glow: "rgba(220,38,38,0.2)" },
];

/* Scenario presets */
const SCENARIOS = [
  { name: "Pilot", icon: Eye, schools: 10, suppliers: 30, txnsPerSchool: 20, avgTxnValue: 1500, auditPacks: 5, partners: 1, schoolGroups: 2, color: "#64748b" },
  { name: "Growth", icon: TrendingUp, schools: 50, suppliers: 120, txnsPerSchool: 40, avgTxnValue: 2500, auditPacks: 15, partners: 3, schoolGroups: 8, color: "#059669" },
  { name: "Scale", icon: BarChart3, schools: 200, suppliers: 350, txnsPerSchool: 60, avgTxnValue: 3500, auditPacks: 40, partners: 5, schoolGroups: 20, color: "#2563eb" },
  { name: "National", icon: Trophy, schools: 500, suppliers: 800, txnsPerSchool: 80, avgTxnValue: 4000, auditPacks: 80, partners: 8, schoolGroups: 50, color: "#7c3aed" },
];

/* ============================================================
   Animated Counter
   ============================================================ */

function AnimatedCounter({ value, prefix = "", suffix = "", duration = 0.8, className = "" }: { value: number; prefix?: string; suffix?: string; duration?: number; className?: string }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => `${prefix}${fmtNum(Math.round(v))}${suffix}`);
  useEffect(() => { const c = animate(mv, value, { duration, ease: "easeOut" }); return c.stop; }, [value, duration, mv]);
  return <motion.span className={className}>{rounded}</motion.span>;
}

/* ============================================================
   Coin Particle
   ============================================================ */

function CoinParticle({ delay, xFrom, xTo, color }: { delay: number; xFrom: string; xTo: number; color: string }) {
  return (
    <motion.div
      className="absolute top-1/2"
      initial={{ left: xFrom, opacity: 0, y: 0, scale: 0.5 }}
      animate={{ left: `${xTo}%`, opacity: [0, 1, 1, 0], y: [0, -15, 5, -10], scale: [0.5, 1, 1, 0.3] }}
      transition={{ duration: 1.4, delay, repeat: Infinity, repeatDelay: 0.3, ease: "easeInOut" }}
    >
      <div className="grid h-5 w-5 place-items-center rounded-full text-[8px] font-bold text-white" style={{ background: color, boxShadow: `0 0 8px ${color}60` }}>
        $
      </div>
    </motion.div>
  );
}

/* ============================================================
   Main Component
   ============================================================ */

type View = "overview" | "simulator" | "projection" | "compare";

export function RevenueModelShowcase() {
  const [view, setView] = useState<View>("overview");
  const [schools, setSchools] = useState(50);
  const [suppliers, setSuppliers] = useState(120);
  const [txnsPerSchool, setTxnsPerSchool] = useState(40);
  const [avgTxnValue, setAvgTxnValue] = useState(2500);
  const [auditPacks, setAuditPacks] = useState(15);
  const [partners, setPartners] = useState(3);
  const [schoolGroups, setSchoolGroups] = useState(8);
  const [activeScenario, setActiveScenario] = useState<string>("Growth");

  const revenue = useMemo(() => {
    const transactionRev = schools * txnsPerSchool * avgTxnValue * STREAMS[0].rate;
    const subscriptionRev = schools * STREAMS[1].rate;
    const verificationRev = suppliers * STREAMS[2].rate * 0.15;
    const auditRev = auditPacks * STREAMS[3].rate;
    const partnerRev = partners * STREAMS[4].rate;
    const implementationRev = schoolGroups * STREAMS[5].rate * 0.1;
    const breakdown = [
      { stream: STREAMS[0], amount: transactionRev },
      { stream: STREAMS[1], amount: subscriptionRev },
      { stream: STREAMS[2], amount: verificationRev },
      { stream: STREAMS[3], amount: auditRev },
      { stream: STREAMS[4], amount: partnerRev },
      { stream: STREAMS[5], amount: implementationRev },
    ];
    const total = breakdown.reduce((s, b) => s + b.amount, 0);
    return { breakdown, total, annual: total * 12 };
  }, [schools, suppliers, txnsPerSchool, avgTxnValue, auditPacks, partners, schoolGroups]);

  const applyScenario = useCallback((s: typeof SCENARIOS[number]) => {
    setActiveScenario(s.name);
    setSchools(s.schools); setSuppliers(s.suppliers); setTxnsPerSchool(s.txnsPerSchool);
    setAvgTxnValue(s.avgTxnValue); setAuditPacks(s.auditPacks); setPartners(s.partners); setSchoolGroups(s.schoolGroups);
  }, []);

  return (
    <Card className="overflow-hidden border-border/60 p-0">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900/40 p-6 sm:p-8">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/30 blur-3xl" />
          <div className="absolute -right-20 top-10 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-48 w-48 rounded-full bg-amber-500/20 blur-3xl" />
        </div>
        <div className="relative">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Badge className="border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
                <Sparkles className="mr-1 h-3 w-3" /> Revenue Model
              </Badge>
              <h3 className="mt-3 font-display text-2xl text-white sm:text-3xl">How BRECCS Makes Money</h3>
              <p className="mt-2 max-w-xl text-sm text-slate-300">
                Six revenue streams across the procurement control plane. Non-custodial by design —
                BRECCS earns from workflow, evidence, and instruction, never from holding money.
              </p>
            </div>
            <div className="flex gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
              {([
                { k: "overview" as const, label: "Streams", icon: PieChart },
                { k: "simulator" as const, label: "Live Sim", icon: Play },
                { k: "projection" as const, label: "Scale", icon: BarChart3 },
                { k: "compare" as const, label: "Compare", icon: Scale },
              ]).map((v) => (
                <button key={v.k} onClick={() => setView(v.k)}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition ${view === v.k ? "bg-emerald-500 text-white shadow-lg" : "text-slate-300 hover:text-white"}`}>
                  <v.icon className="h-3.5 w-3.5" />{v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scenario quick-select */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Quick Scenario:</span>
            {SCENARIOS.map((s) => (
              <button key={s.name} onClick={() => applyScenario(s)}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  activeScenario === s.name ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 bg-white/5 text-slate-400 hover:text-white"}`}>
                <s.icon className="h-3 w-3" style={{ color: s.color }} />{s.name}
              </button>
            ))}
          </div>

          {/* Total revenue banner */}
          <div className="mt-5 flex flex-wrap items-end gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Monthly Revenue</div>
              <div className="font-display text-3xl text-emerald-400 sm:text-4xl">
                <AnimatedCounter value={revenue.total} prefix="USD " duration={1.2} />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Annual Run-Rate</div>
              <div className="font-display text-2xl text-white sm:text-3xl">
                <AnimatedCounter value={revenue.annual} prefix="USD " duration={1.5} />
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/10 px-4 py-2">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <span className="text-sm text-emerald-300">
                <AnimatedCounter value={schools} /> schools · <AnimatedCounter value={suppliers} /> suppliers
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6">
        <AnimatePresence mode="wait">
          {view === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <OverviewView breakdown={revenue.breakdown} total={revenue.total} />
            </motion.div>
          )}
          {view === "simulator" && (
            <motion.div key="sim" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <SimulatorView />
            </motion.div>
          )}
          {view === "projection" && (
            <motion.div key="proj" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <ProjectionView schools={schools} setSchools={setSchools} suppliers={suppliers} setSuppliers={setSuppliers}
                txnsPerSchool={txnsPerSchool} setTxnsPerSchool={setTxnsPerSchool} avgTxnValue={avgTxnValue} setAvgTxnValue={setAvgTxnValue}
                auditPacks={auditPacks} setAuditPacks={setAuditPacks} partners={partners} setPartners={setPartners}
                schoolGroups={schoolGroups} setSchoolGroups={setSchoolGroups} breakdown={revenue.breakdown} total={revenue.total} annual={revenue.annual} />
            </motion.div>
          )}
          {view === "compare" && (
            <motion.div key="cmp" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <CompareView total={revenue.total} annual={revenue.annual} schools={schools} txnsPerSchool={txnsPerSchool} avgTxnValue={avgTxnValue} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
}

/* ============================================================
   Overview View — Donut + Stream Cards + Revenue Vault
   ============================================================ */

function OverviewView({ breakdown, total }: { breakdown: { stream: RevenueStream; amount: number }[]; total: number }) {
  const maxStream = Math.max(...breakdown.map((b) => b.amount));
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
      <div className="flex flex-col items-center justify-center gap-4">
        <RevenueDonut breakdown={breakdown} total={total} />
        <RevenueVault total={total} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {breakdown.map((b, i) => (
          <motion.div key={b.stream.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }}
            className="group relative overflow-hidden rounded-xl border p-4 transition hover:shadow-lg" style={{ borderColor: `${b.stream.color}30` }}>
            <div className="absolute inset-0 opacity-0 transition group-hover:opacity-100" style={{ background: `radial-gradient(circle at top right, ${b.stream.glow}, transparent 70%)` }} />
            <div className="relative">
              <div className="flex items-start justify-between">
                <div className={`grid h-9 w-9 place-items-center rounded-lg ${b.stream.bg}`}>
                  <b.stream.icon className="h-4 w-4" style={{ color: b.stream.color }} />
                </div>
                <Badge variant="outline" className="text-[9px]" style={{ color: b.stream.color, borderColor: `${b.stream.color}40` }}>{b.stream.unitLabel}</Badge>
              </div>
              <div className="mt-2.5 text-sm font-semibold">{b.stream.name}</div>
              <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{b.stream.description}</div>
              <div className="mt-2.5 flex items-end justify-between">
                <div className="font-display text-lg" style={{ color: b.stream.color }}>{fmtUSD(b.amount)}</div>
                <div className="text-[10px] text-muted-foreground">{total > 0 ? ((b.amount / total) * 100).toFixed(1) : "0"}%</div>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <motion.div className="h-full rounded-full" style={{ background: b.stream.color }} initial={{ width: 0 }} animate={{ width: `${(b.amount / maxStream) * 100}%` }} transition={{ duration: 0.6, delay: i * 0.06 }} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* Revenue Vault — animated filling vault */
function RevenueVault({ total }: { total: number }) {
  const fillPct = Math.min(100, (total / 500000) * 100);
  return (
    <div className="relative h-28 w-48 overflow-hidden rounded-2xl border-2 border-emerald-300/30 bg-gradient-to-b from-slate-800 to-slate-900">
      {/* Coins inside */}
      <motion.div className="absolute bottom-0 left-0 right-0 rounded-b-2xl bg-gradient-to-t from-emerald-500/40 to-emerald-400/20"
        initial={{ height: 0 }} animate={{ height: `${fillPct}%` }} transition={{ duration: 1, ease: "easeOut" }}>
        {/* Floating coins */}
        {[...Array(6)].map((_, i) => (
          <motion.div key={i} className="absolute grid h-4 w-4 place-items-center rounded-full bg-amber-400 text-[7px] font-bold text-amber-900"
            style={{ left: `${15 + i * 14}%`, bottom: `${5 + (i % 3) * 12}%` }}
            animate={{ y: [0, -4, 0], rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2 + i * 0.3, repeat: Infinity, delay: i * 0.2 }}>
            $
          </motion.div>
        ))}
      </motion.div>
      {/* Vault label */}
      <div className="absolute left-0 right-0 top-0 p-2 text-center">
        <div className="text-[9px] uppercase tracking-wider text-slate-400">Revenue Vault</div>
        <div className="font-display text-sm text-emerald-400">{fmtUSD(total)}/mo</div>
      </div>
      {/* Vault door bars */}
      <div className="absolute right-2 top-2 flex flex-col gap-0.5">
        {[...Array(4)].map((_, i) => <div key={i} className="h-0.5 w-6 rounded-full bg-slate-600" />)}
      </div>
    </div>
  );
}

/* SVG Donut */
function RevenueDonut({ breakdown, total }: { breakdown: { stream: RevenueStream; amount: number }[]; total: number }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const radius = 80; const stroke = 28; const circ = 2 * Math.PI * radius; let offset = 0;
  return (
    <div className="relative">
      <svg width={220} height={220} viewBox="0 0 220 220">
        <circle cx={110} cy={110} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} opacity={0.3} />
        {breakdown.map((b, i) => {
          const frac = total > 0 ? b.amount / total : 0; const dash = frac * circ;
          const seg = (
            <motion.circle key={b.stream.id} cx={110} cy={110} r={radius} fill="none" stroke={b.stream.color}
              strokeWidth={hovered === i ? stroke + 4 : stroke} strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset}
              transform="rotate(-90 110 110)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.08 }}
              style={{ cursor: "pointer", transition: "stroke-width 0.2s" }}
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} />
          );
          offset += dash; return seg;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {hovered !== null ? (
          <motion.div key={hovered} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{breakdown[hovered].stream.name}</div>
            <div className="font-display text-xl" style={{ color: breakdown[hovered].stream.color }}>{fmtUSD(breakdown[hovered].amount)}</div>
            <div className="text-[10px] text-muted-foreground">{((breakdown[hovered].amount / total) * 100).toFixed(1)}%</div>
          </motion.div>
        ) : (
          <div className="text-center">
            <Coins className="mx-auto h-6 w-6 text-amber-500" />
            <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">6 Streams</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Simulator View — Animated Pipeline with Coin Flow
   ============================================================ */

type SimPhase = "idle" | "requisition" | "approve" | "receive" | "invoice" | "match" | "pay" | "settle" | "done";

const SIM_PHASES: { phase: SimPhase; label: string; desc: string; icon: typeof Zap; color: string }[] = [
  { phase: "requisition", label: "Requisition", desc: "Requester submits need with budget justification", icon: FileCheck, color: "#2563eb" },
  { phase: "approve", label: "PO Approved", desc: "Procurement officer approves purchase order", icon: ShieldCheck, color: "#059669" },
  { phase: "receive", label: "Goods Received", desc: "Receiver confirms delivery with offline GRN", icon: Building2, color: "#d97706" },
  { phase: "invoice", label: "Invoice", desc: "Supplier invoice linked to PO and receipt", icon: FileCheck, color: "#7c3aed" },
  { phase: "match", label: "3-Way Match", desc: "PO + receipt + invoice reconciled automatically", icon: ShieldCheck, color: "#0891b2" },
  { phase: "pay", label: "Payment", desc: "Approved payable becomes signed payment instruction", icon: Zap, color: "#dc2626" },
  { phase: "settle", label: "Settlement", desc: "Licensed partner executes and confirms settlement", icon: Layers, color: "#059669" },
];

function SimulatorView() {
  const [phase, setPhase] = useState<SimPhase>("idle");
  const [amount, setAmount] = useState(15000);
  const [revenueEarned, setRevenueEarned] = useState(0);
  const [txns, setTxns] = useState(0);
  const [history, setHistory] = useState<{ id: string; amount: number; fee: number }[]>([]);
  const timers = useRef<number[]>([]);

  const txFee = Math.round(amount * STREAMS[0].rate);
  const subscriptionAlloc = Math.round(STREAMS[1].rate / 40);
  const auditAlloc = Math.round(STREAMS[3].rate / 15);
  const totalFee = txFee + subscriptionAlloc + auditAlloc;

  const reset = useCallback(() => { timers.current.forEach((t) => window.clearTimeout(t)); timers.current = []; setPhase("idle"); setRevenueEarned(0); setTxns(0); setHistory([]); }, []);

  const run = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t)); timers.current = []; setPhase("requisition");
    const seq: Array<[number, () => void]> = []; let t = 0;
    const at = (ms: number, fn: () => void) => { t += ms; seq.push([t, fn]); };
    at(650, () => setPhase("approve")); at(650, () => setPhase("receive")); at(650, () => setPhase("invoice"));
    at(650, () => setPhase("match")); at(550, () => setPhase("pay")); at(750, () => setPhase("settle"));
    at(550, () => {
      setPhase("done"); setRevenueEarned((r) => r + totalFee); setTxns((c) => c + 1);
      setHistory((h) => [{ id: `TXN-${String(txns + 1).padStart(4, "0")}`, amount, fee: totalFee }, ...h].slice(0, 5));
    });
    at(900, () => setPhase("idle"));
    seq.forEach(([ms, fn]) => timers.current.push(window.setTimeout(fn, ms)));
  }, [amount, totalFee, txns]);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  const activeIndex = SIM_PHASES.findIndex((p) => p.phase === phase);
  const isFlowing = phase !== "idle" && phase !== "done";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Transaction Value</span>
        {[5000, 15000, 50000, 250000].map((p) => (
          <Button key={p} variant={amount === p ? "default" : "outline"} size="sm" onClick={() => setAmount(p)} className="text-xs">{fmtUSD(p)}</Button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={reset}><RotateCcw className="mr-1 h-3 w-3" />Reset</Button>
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={run} disabled={phase !== "idle"}>
            <Play className="mr-1 h-3 w-3" />Run Transaction
          </Button>
        </div>
      </div>

      {/* Pipeline with coin flow */}
      <div className="relative rounded-2xl border bg-gradient-to-br from-muted/20 to-transparent p-5">
        {/* Coin particles flowing left to right */}
        {isFlowing && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {[0, 0.4, 0.8, 1.2, 1.6].map((d, i) => (
              <CoinParticle key={i} delay={d} xFrom="8%" xTo={85} color="#fbbf24" />
            ))}
          </div>
        )}

        {/* Progress bar */}
        <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-muted">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-blue-500 to-amber-500"
            initial={{ width: 0 }} animate={{ width: phase === "idle" ? 0 : `${((activeIndex + 1) / SIM_PHASES.length) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>

        {/* Phase steps with icons */}
        <div className="relative grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {SIM_PHASES.map((p, i) => {
            const isActive = i === activeIndex;
            const isDone = activeIndex > i || phase === "done";
            return (
              <motion.div key={p.phase} animate={{ scale: isActive ? 1.08 : 1, y: isActive ? -4 : 0 }}
                className={`flex flex-col items-center rounded-lg border p-2.5 text-center transition ${
                  isActive ? "border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-400/30" :
                  isDone ? "border-emerald-200 bg-emerald-50/20" : "border-border bg-background"}`}>
                <motion.div className={`grid h-9 w-9 place-items-center rounded-full ${isDone ? "bg-emerald-500 text-white" : isActive ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}
                  animate={{ boxShadow: isActive ? `0 0 12px ${p.color}60` : "0 0 0px transparent" }}>
                  <p.icon className="h-4 w-4" />
                </motion.div>
                <div className="mt-1.5 text-[10px] font-medium leading-tight">{p.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Active phase description */}
        <AnimatePresence mode="wait">
          {phase !== "idle" && phase !== "done" && activeIndex >= 0 && (
            <motion.div key={phase} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50/40 p-3 text-sm">
              <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="text-muted-foreground">{SIM_PHASES[activeIndex].desc}</span>
            </motion.div>
          )}
          {phase === "done" && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span className="font-medium text-emerald-700">Transaction complete — BRECCS earned {fmtUSD(totalFee)} from this flow.</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Revenue extraction with coin icons */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <RevenueExtractCard icon={Zap} label="Transaction Fee" rate="1.5% of flow" amount={txFee} color="#059669" bg="bg-emerald-500/10" active={phase === "pay" || phase === "settle" || phase === "done"} />
        <RevenueExtractCard icon={Building2} label="Subscription Alloc." rate="USD 450/mo ÷ 40 txns" amount={subscriptionAlloc} color="#2563eb" bg="bg-blue-500/10" active={phase === "requisition" || phase === "approve"} />
        <RevenueExtractCard icon={FileCheck} label="Audit Pack Alloc." rate="USD 120 ÷ 15 txns" amount={auditAlloc} color="#7c3aed" bg="bg-violet-500/10" active={phase === "match" || phase === "invoice"} />
      </div>

      {/* Cumulative + history */}
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-emerald-50/40 to-transparent p-4">
          <div className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-emerald-600" />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">BRECCS Revenue This Session</div>
          </div>
          <div className="mt-1 font-display text-3xl text-emerald-600"><AnimatedCounter value={revenueEarned} prefix="USD " /></div>
          <div className="mt-1 text-xs text-muted-foreground">{txns} transaction{txns !== 1 ? "s" : ""} processed</div>
          {/* Mini coin pile */}
          <div className="mt-2 flex flex-wrap gap-1">
            {[...Array(Math.min(txns, 12))].map((_, i) => (
              <motion.div key={i} initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: i * 0.05 }}
                className="grid h-5 w-5 place-items-center rounded-full bg-amber-400 text-[7px] font-bold text-amber-900 shadow-sm">$</motion.div>
            ))}
          </div>
        </div>
        {history.length > 0 && (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr><th className="px-3 py-2 text-left">Txn ID</th><th className="px-3 py-2 text-right">Flow Value</th><th className="px-3 py-2 text-right">BRECCS Earned</th></tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {history.map((t) => (
                    <motion.tr key={t.id} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="border-t">
                      <td className="px-3 py-2 font-mono text-[11px]">{t.id}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtUSD(t.amount)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">+{fmtUSD(t.fee)}</td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RevenueExtractCard({ icon: Icon, label, rate, amount, color, bg, active }: { icon: typeof Zap; label: string; rate: string; amount: number; color: string; bg: string; active: boolean }) {
  return (
    <motion.div animate={{ scale: active ? 1.03 : 1 }} className={`rounded-xl border p-3 transition ${active ? "ring-2" : ""}`} style={{ borderColor: `${color}30`, boxShadow: active ? `0 0 16px ${color}20` : "none" }}>
      <div className="flex items-center justify-between">
        <div className={`grid h-8 w-8 place-items-center rounded-lg ${bg}`}><Icon className="h-4 w-4" style={{ color }} /></div>
        <motion.div animate={{ opacity: active ? [0.5, 1, 0.5] : 1 }} transition={{ duration: 1, repeat: active ? Infinity : 0 }} className="h-2 w-2 rounded-full" style={{ background: active ? color : "transparent" }} />
      </div>
      <div className="mt-2 text-xs font-medium">{label}</div>
      <div className="text-[10px] text-muted-foreground">{rate}</div>
      <div className="mt-1.5 font-display text-lg" style={{ color }}>{fmtUSD(amount)}</div>
    </motion.div>
  );
}

/* ============================================================
   Projection View
   ============================================================ */

function ProjectionView({ schools, setSchools, suppliers, setSuppliers, txnsPerSchool, setTxnsPerSchool, avgTxnValue, setAvgTxnValue, auditPacks, setAuditPacks, partners, setPartners, schoolGroups, setSchoolGroups, breakdown, total, annual }: {
  schools: number; setSchools: (v: number) => void; suppliers: number; setSuppliers: (v: number) => void;
  txnsPerSchool: number; setTxnsPerSchool: (v: number) => void; avgTxnValue: number; setAvgTxnValue: (v: number) => void;
  auditPacks: number; setAuditPacks: (v: number) => void; partners: number; setPartners: (v: number) => void;
  schoolGroups: number; setSchoolGroups: (v: number) => void; breakdown: { stream: RevenueStream; amount: number }[]; total: number; annual: number;
}) {
  const maxStream = Math.max(...breakdown.map((b) => b.amount));
  const scales = [
    { label: "Pilot", schools: 10, multiplier: 10 / 50 },
    { label: "Year 1", schools: 50, multiplier: 1 },
    { label: "Year 2", schools: 200, multiplier: 200 / 50 },
    { label: "Year 3", schools: 500, multiplier: 500 / 50 },
    { label: "Year 5", schools: 1500, multiplier: 1500 / 50 },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
      <div className="space-y-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Adjust Your Scale</div>
        <SliderControl icon={Building2} label="Schools" value={schools} onChange={setSchools} min={5} max={500} step={5} color="#2563eb" />
        <SliderControl icon={Users} label="Suppliers" value={suppliers} onChange={setSuppliers} min={10} max={500} step={10} color="#d97706" />
        <SliderControl icon={Zap} label="Txns / school / month" value={txnsPerSchool} onChange={setTxnsPerSchool} min={5} max={100} step={5} color="#059669" />
        <SliderControl icon={DollarSign} label="Avg. transaction value" value={avgTxnValue} onChange={setAvgTxnValue} min={500} max={20000} step={500} color="#0891b2" format={(v) => fmtUSD(v)} />
        <SliderControl icon={FileCheck} label="Audit packs / month" value={auditPacks} onChange={setAuditPacks} min={0} max={100} step={5} color="#7c3aed" />
        <SliderControl icon={Layers} label="Settlement partners" value={partners} onChange={setPartners} min={1} max={10} step={1} color="#dc2626" />
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="rounded-xl border bg-gradient-to-br from-emerald-50/40 to-transparent p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Monthly</div>
            <div className="font-display text-2xl text-emerald-600">{fmtUSD(total)}</div>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-blue-50/40 to-transparent p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual</div>
            <div className="font-display text-2xl text-blue-600">{fmtUSD(annual)}</div>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Revenue by Stream</div>
          <div className="space-y-2.5">
            {breakdown.map((b, i) => (
              <div key={b.stream.id} className="flex items-center gap-3">
                <div className="w-32 shrink-0 text-right text-xs font-medium">{b.stream.name}</div>
                <div className="relative h-7 flex-1 overflow-hidden rounded-lg bg-muted/30">
                  <motion.div className="flex h-full items-center justify-end rounded-lg pr-2 text-[10px] font-medium text-white" style={{ background: b.stream.color }}
                    initial={{ width: 0 }} animate={{ width: `${Math.max(8, (b.amount / maxStream) * 100)}%` }} transition={{ duration: 0.5, delay: i * 0.05 }}>
                    {b.amount > maxStream * 0.15 && fmtUSD(b.amount)}
                  </motion.div>
                </div>
                <div className="w-12 shrink-0 text-right text-[10px] text-muted-foreground">{((b.amount / total) * 100).toFixed(0)}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Growth chart */}
        <div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Growth Trajectory</div>
          <div className="flex items-end gap-2 rounded-xl border bg-gradient-to-b from-emerald-50/20 to-transparent p-4" style={{ height: 140 }}>
            {scales.map((s, i) => {
              const scaledTotal = total * s.multiplier;
              const maxScale = total * (1500 / 50);
              const barHeight = Math.max(4, (scaledTotal / maxScale) * 100);
              const isCurrent = s.schools === schools;
              return (
                <div key={s.label} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-[8px] font-mono text-muted-foreground">{fmtUSD(scaledTotal)}</div>
                  <motion.div className="w-full rounded-t-md" style={{ background: isCurrent ? "#059669" : "#2563eb" }}
                    initial={{ height: 0 }} animate={{ height: `${barHeight}%` }} transition={{ duration: 0.5, delay: i * 0.1 }} />
                  <div className={`text-[9px] font-medium ${isCurrent ? "text-emerald-600" : "text-muted-foreground"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr><th className="px-3 py-2 text-left">Stage</th><th className="px-3 py-2 text-right">Schools</th><th className="px-3 py-2 text-right">Monthly</th><th className="px-3 py-2 text-right">Annual</th></tr>
            </thead>
            <tbody>
              {scales.map((s) => {
                const scaledTotal = total * s.multiplier; const isCurrent = s.schools === schools;
                return (
                  <tr key={s.label} className={`border-t ${isCurrent ? "bg-emerald-50/30" : ""}`}>
                    <td className="px-3 py-2.5 font-medium">{s.label}{isCurrent && <Badge variant="outline" className="ml-2 text-[9px] text-emerald-600 border-emerald-400/40">Current</Badge>}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtNum(s.schools)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-600">{fmtUSD(scaledTotal)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-blue-600">{fmtUSD(scaledTotal * 12)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-amber-300/30 bg-amber-50/30 p-4">
          <Crown className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Key insight:</span> Transaction fees scale linearly with procurement volume, while subscriptions and partner fees provide predictable recurring revenue.
            At {fmtNum(schools)} schools processing {txnsPerSchool} transactions/month at {fmtUSD(avgTxnValue)} average,
            BRECCS earns <span className="font-semibold text-emerald-600">{fmtUSD(annual)}/year</span> — without ever holding customer funds.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Compare View — BRECCS vs Traditional vs Manual
   ============================================================ */

function CompareView({ total, annual, schools, txnsPerSchool, avgTxnValue }: { total: number; annual: number; schools: number; txnsPerSchool: number; avgTxnValue: number }) {
  const monthlyFlow = schools * txnsPerSchool * avgTxnValue;
  const breccsFee = total;
  const traditionalFee = monthlyFlow * 0.035; // 3.5% traditional payment processor
  const manualCost = monthlyFlow * 0.08 + schools * 200; // 8% leakage + admin overhead

  const comparisons = [
    { name: "BRECCS Control Plane", fee: breccsFee, color: "#059669", icon: ShieldCheck, pros: ["Non-custodial — never holds money", "Full audit trail built-in", "Segregation of duties enforced", "Dual-currency USD/ZiG tracking"], cons: ["Requires partner onboarding"] },
    { name: "Traditional Payment Processor", fee: traditionalFee, color: "#dc2626", icon: CreditCard, pros: ["Fast settlement", "Known infrastructure"], cons: ["No procurement controls", "No audit trail", "No segregation of duties", "Custodial risk — holds funds", "3.5%+ per transaction"] },
    { name: "Manual / Paper-Based", fee: manualCost, color: "#d97706", icon: FileCheck, pros: ["No software cost"], cons: ["8%+ leakage from fraud & errors", "No real-time visibility", "Audit takes weeks to reconstruct", "Headmaster controls entire flow", "No currency truth"] },
  ];

  const maxFee = Math.max(...comparisons.map((c) => c.fee));

  return (
    <div className="space-y-5">
      <div className="text-center">
        <Badge variant="outline" className="text-xs"><MousePointerClick className="mr-1 h-3 w-3" /> Cost Comparison · Monthly Flow: {fmtUSD(monthlyFlow)}</Badge>
      </div>

      {/* Bar comparison */}
      <div className="space-y-3">
        {comparisons.map((c, i) => (
          <motion.div key={c.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
            className={`rounded-xl border p-4 ${i === 0 ? "ring-2 ring-emerald-400/30" : ""}`} style={{ borderColor: `${c.color}30` }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${c.color}15` }}>
                  <c.icon className="h-4 w-4" style={{ color: c.color }} />
                </div>
                <span className="text-sm font-semibold">{c.name}</span>
                {i === 0 && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-700 border-emerald-400/30">Recommended</Badge>}
              </div>
              <div className="font-display text-lg" style={{ color: c.color }}>{fmtUSD(c.fee)}/mo</div>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-muted">
              <motion.div className="h-full rounded-full" style={{ background: c.color }}
                initial={{ width: 0 }} animate={{ width: `${(c.fee / maxFee) * 100}%` }} transition={{ duration: 0.6, delay: i * 0.1 }} />
            </div>
            <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-emerald-600">Advantages</div>
                {c.pros.map((p, j) => <div key={j} className="flex items-center gap-1 text-[10px] text-muted-foreground"><ShieldCheck className="h-2.5 w-2.5 text-emerald-500 shrink-0" />{p}</div>)}
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-rose-600">Disadvantages</div>
                {c.cons.map((con, j) => <div key={j} className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="h-2.5 w-2.5 shrink-0 text-rose-500">✕</span>{con}</div>)}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Savings callout */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
        className="rounded-2xl border-2 border-emerald-400/30 bg-gradient-to-r from-emerald-50/50 via-transparent to-blue-50/50 p-5 text-center">
        <div className="flex items-center justify-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          <span className="text-sm font-semibold text-emerald-700">
            BRECCS saves {fmtUSD(traditionalFee - breccsFee)}/mo vs traditional processors
          </span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          That's <span className="font-semibold text-emerald-600">{fmtUSD((traditionalFee - breccsFee) * 12)}/year</span> in savings —
          plus full procurement controls, audit trail, and zero custodial risk.
        </div>
      </motion.div>
    </div>
  );
}

/* SliderControl */
function SliderControl({ icon: Icon, label, value, onChange, min, max, step, color, format }: { icon: typeof Building2; label: string; value: number; onChange: (v: number) => void; min: number; max: number; step: number; color: string; format?: (v: number) => string }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium"><Icon className="h-3.5 w-3.5" style={{ color }} />{label}</div>
        <span className="font-mono text-xs font-semibold" style={{ color }}>{format ? format(value) : fmtNum(value)}</span>
      </div>
      <Slider value={[value]} onValueChange={(v) => onChange(v[0])} min={min} max={max} step={step} />
    </div>
  );
}
